/**
 * Клиент API кабинета.
 *
 * Что здесь принципиально и почему.
 *
 * * **Один источник имени.** Кабинет и API стоят за `app.seeno.ru`, API — по
 *   префиксу `/api/v1/`. Поэтому запросы идут относительными адресами, CORS не
 *   нужен, а куки host-only. Ни одного обращения на другой домен в коде нет и
 *   быть не должно: на портале агентства бандл отдаётся с домена агентства, и
 *   зашитый origin сломал бы вход именно там.
 * * **Заголовка Authorization нет.** Токен живёт в куке `HttpOnly` — скрипт её
 *   не читает по определению. Отсюда `credentials: 'same-origin'`.
 * * **Организация не передаётся клиентом.** Активный арендатор берётся из
 *   токена; переключение — только `POST /auth/context`.
 * * **Обновление сессии невидимо.** На `401`, который может лечиться обменом,
 *   клиент один раз идёт в `/auth/refresh` и повторяет запрос — **любой**, а не
 *   только безопасный: `401` означает, что сервер до обработчика не дошёл.
 *   Какие именно `401` лечатся обменом — `REFRESHABLE`.
 * * **Курсорные страницы.** Offset нет нигде, `next_cursor: null` — конец.
 */

import {
  ApiError,
  isProblem,
  offlineProblem,
  unreadableProblem,
  type Problem,
} from './problem';

const BASE = '/api/v1';

/**
 * Коды `401`, после которых имеет смысл сходить за новым access.
 *
 * `unauthenticated` в этом списке — не перестраховка, а единственный код,
 * который кабинет вообще видит, когда истекает вход. Куку access сервер ставит
 * с `Max-Age`, равным сроку жизни самого токена (15 минут), поэтому браузер
 * удаляет её РОВНО ТОГДА, когда токен протухает: до сервера просроченный токен
 * не доезжает никогда, и `session_expired` в браузере недостижим. Пока в
 * условии стоял только он, кабинет на шестнадцатой минуте выбрасывал человека
 * на форму входа при живой refresh-куке на тридцать суток — проверено на
 * приёмке трижды, в том числе посреди оплаты.
 *
 * Обмен здесь именно попытка: не вышло — ниже честно объявляется потеря сессии.
 * `invalid_credentials` и `mfa_required` сюда не входят: это обычные ответы
 * формы входа, а не потерянная сессия.
 */
const REFRESHABLE = new Set(['session_expired', 'unauthenticated']);

/**
 * Коды `401`, которые форма входа и подтверждение действия получают в обычной
 * работе. Это ответ формы, а не пропавшая сессия: объявлять по ним потерю значит
 * уводить со страницы входа на страницу входа, стирая набранное.
 */
const EXPECTED_ON_FORMS = new Set([
  'invalid_credentials',
  'mfa_required',
  'account_locked',
]);

export interface RequestOptions {
  method?: string;
  /** Тело запроса, сериализуется в JSON. */
  body?: unknown;
  /** Параметры строки запроса; `undefined` и `null` не отправляются. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Ключ идемпотентности. Обязателен на 26 создающих операциях со внешним
   * эффектом: платный вызов к движку, списание, письмо, приглашение, публичная
   * ссылка. Значение — ULID или uuid4, живёт семь суток.
   */
  idempotencyKey?: string;
  /** Токен подтверждения действия, полученный из step-up. */
  stepUpToken?: string;
  /**
   * Не объявлять потерю сессии на 401. Ставится там, где отсутствие сессии —
   * ожидаемый ответ, а не событие: первая проверка при загрузке кабинета и
   * публичные страницы приглашения. Иначе обычный приход неаутентифицированного
   * человека выглядел бы как «вас выкинуло».
   */
  quiet?: boolean;
  signal?: AbortSignal;
}

/** Ответ курсорной ленты. `next_cursor: null` — данные кончились. */
export interface Page<T> {
  items: T[];
  next_cursor: string | null;
  limit: number;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Обещает ли ответ JSON.
 *
 * Проверяется суффикс `+json` и тип `application/json`, а не вхождение
 * подстроки «json» куда попало: `text/html` от края CDN и `text/plain` от
 * заглушки провайдера обязаны читаться как «не JSON», даже если слово json
 * встретится в параметрах заголовка.
 */
function promisesJson(response: Response): boolean {
  const type = (response.headers.get('content-type') ?? '').toLowerCase();
  const essence = type.split(';', 1)[0]!.trim();
  return essence === 'application/json' || essence.endsWith('+json');
}

async function readProblem(response: Response): Promise<Problem> {
  if (promisesJson(response)) {
    try {
      const body: unknown = await response.json();
      if (isProblem(body)) {
        // Код ответа берётся из HTTP, а не из тела. По RFC 9457 поле `status`
        // в теле — справочное и обязано совпадать с кодом ответа; когда они
        // расходятся, верен HTTP, потому что по нему уже отработали прокси и
        // кеши. Клиент, доверяющий телу, ломается ровно там, где ошибку
        // сгенерировали не мы: подменный сервер, шлюз, страница провайдера.
        return { ...body, status: response.status };
      }
    } catch {
      // тело не разобралось — соберём проблему сами ниже
    }
  }
  return unreadableProblem(
    response.status,
    response.headers.get('x-request-id') ?? undefined,
  );
}

let refreshing: Promise<boolean> | null = null;

/**
 * Обновление сессии. Параллельные 401 не должны порождать несколько обменов:
 * ротация refresh помечает старый использованным, а повторное предъявление
 * использованного трактуется как кража и отзывает всё семейство. Поэтому обмен
 * ровно один, а остальные ждут его результата.
 */
async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Следующий 401 должен уметь запустить новый обмен.
      window.setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();
  return refreshing;
}

async function once(path: string, options: RequestOptions): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.stepUpToken) headers['X-Seeno-Step-Up'] = options.stepUpToken;

  return fetch(buildUrl(path, options.query), {
    method,
    headers,
    credentials: 'same-origin',
    signal: options.signal,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
}

/**
 * Сессия кончилась окончательно: refresh не обменялся или сервер сказал, что
 * куки нет вовсе.
 *
 * Такое нельзя показывать как ошибку экрана — «не получилось загрузить» вместо
 * формы входа означает, что человек будет обновлять страницу, пока не догадается
 * сам. Оболочка подписывается на это событие и уводит на вход, сохранив, куда
 * он шёл.
 *
 * Коды входа и восстановления сюда не попадают: `invalid_credentials` на форме
 * входа — это обычный ответ формы, а не потеря сессии.
 */
type SessionLostListener = () => void;
const sessionLostListeners = new Set<SessionLostListener>();

export function onSessionLost(listener: SessionLostListener): () => void {
  sessionLostListeners.add(listener);
  return () => sessionLostListeners.delete(listener);
}

function announceSessionLost(): void {
  for (const listener of sessionLostListeners) listener();
}

/** Заголовок `Retry-After` в секундах. Им сервер задаёт частоту опроса. */
function retryAfterOf(response: Response): number | undefined {
  const raw = response.headers.get('retry-after');
  if (!raw) return undefined;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

export interface Result<T> {
  body: T;
  /**
   * Рекомендованный интервал следующего опроса. В терминальном ответе его нет —
   * это и есть сигнал, что опрашивать больше нечего (docs/API.md §8).
   */
  retryAfter?: number;
  /**
   * Заголовки ответа целиком.
   *
   * Нужны там, где служебное значение приходит **только** заголовком и в теле
   * его нет: `X-Seeno-Audit-Session` отдаётся один раз, в ответе на запуск
   * бесплатного аудита, и без него нечем доказать право присвоить аудит.
   * Отдаём объект `Headers`, а не разобранные поля: перечислять здесь каждый
   * интересный заголовок значит править этот файл на каждый новый случай.
   */
  headers: Headers;
}

/**
 * Один запрос к API вместе со служебными полями ответа. Нужен там, где важен
 * не только разобранный ответ: прогресс замера тянется опросом, и интервал
 * задаёт сервер, а не мы.
 */
export async function requestWith<T>(
  path: string,
  options: RequestOptions = {},
): Promise<Result<T>> {
  let response: Response;
  try {
    response = await once(path, options);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(offlineProblem());
  }

  if (response.status === 401) {
    const problem = await readProblem(response);
    // Повторяется ЛЮБОЙ метод, а не только безопасный.
    //
    // Здесь стояла проверка «повторяем, если метод безопасен или несёт ключ
    // идемпотентности», и она выбрасывала человека из кабинета посреди работы.
    // На приёмке: двадцать сгенерированных вопросов, «Добавить в ядро» на
    // шестнадцатой минуте — форма входа, кандидаты пропали, refresh-кука жива
    // (следующий заход молча вернул в кабинет). Ключ идемпотентности передают
    // три модуля из десяти, то есть двадцать вызовов кабинета — вопросы, бренды,
    // конкуренты, синонимы, прогоны, онбординг — теряли работу на первом же
    // истёкшем access.
    //
    // Идемпотентность к этому отношения не имеет. Ключ защищает от ПОВТОРНОГО
    // ВЫПОЛНЕНИЯ, а `401` означает, что выполнения не было вовсе: запрос отбит
    // аутентификацией до обработчика, тело даже не разобрано. Повторить такой
    // запрос ровно так же безопасно, как отправить его впервые, — потому что
    // впервые он и отправляется.
    //
    // Повтор строго один. Второй `401` подряд — это уже не протухший access, а
    // отозванный вход, и он честно объявляется потерей сессии.
    if (REFRESHABLE.has(problem.code) && (await refreshSession())) {
      response = await once(path, options);
      if (response.status === 401) {
        // Обменялись, а всё равно 401. Обычно это конец сессии — но не всегда:
        // повтор УЖЕ прошёл аутентификацию, дошёл до обработчика и мог получить
        // законный отказ формы. Смена пароля с неверным текущим и подтверждение
        // действия неверным паролем отвечают `invalid_credentials` именно так.
        // Объявлять по ним потерю сессии значит разлогинивать человека за
        // опечатку в пароле — и терять заодно всё, что он набрал в форме.
        const retried = await readProblem(response);
        if (!options.quiet && !EXPECTED_ON_FORMS.has(retried.code)) announceSessionLost();
        throw new ApiError(retried);
      }
    } else {
      // Обменять не вышло или куки нет вовсе. Форму входа за это отвечает
      // оболочка; здесь мы только сообщаем, что сессии больше нет.
      if (!options.quiet && !EXPECTED_ON_FORMS.has(problem.code)) announceSessionLost();
      throw new ApiError(problem);
    }
  }

  if (!response.ok) throw new ApiError(await readProblem(response));

  const retryAfter = retryAfterOf(response);

  // Ответ без тела — это не только `204`. Контракт объявляет `202 Accepted` без
  // содержимого у всего, что продолжается письмом или очередью: регистрация,
  // повторная отправка подтверждения, запрос выгрузки. Здесь разбирался
  // единственный `204`, а `202` уходил в `response.json()` и падал на пустой
  // строке — то есть **успешная** регистрация показывалась человеку как «Что-то
  // не сработало на нашей стороне».
  //
  // Проверяется наличие тела, а не список кодов: перечисление кодов пришлось бы
  // дополнять при каждом новом бесстоловом ответе, и однажды его забудут.
  if (response.status === 204 || response.status === 205) {
    return { body: undefined as T, retryAfter, headers: response.headers };
  }
  const text = await response.text();
  if (text === '') return { body: undefined as T, retryAfter, headers: response.headers };

  // Успех с телом обязан быть JSON. Разбирать что попало нельзя: `JSON.parse`
  // на `<!doctype html>` бросает `SyntaxError` — обычное исключение, а не
  // `ApiError`, — и оно пролетает мимо всей модели ошибок. Вызывающий код
  // ветвится по `code`, у `SyntaxError` его нет; экран показывает «Что-то не
  // сработало на нашей стороне» вместо разбираемой проблемы, а в консоли
  // остаётся голая ошибка разбора без кода ответа и без `x-request-id`.
  //
  // Проверяется тип содержимого, а не адрес: успешный не-JSON приходит от
  // того, кто стоит перед сервисом (край CDN, прокси, портал Wi-Fi), и по телу
  // его не отличить от нашего ответа заранее.
  const requestId = response.headers.get('x-request-id') ?? undefined;
  if (!promisesJson(response)) {
    throw new ApiError(unreadableProblem(response.status, requestId));
  }
  try {
    return { body: JSON.parse(text) as T, retryAfter, headers: response.headers };
  } catch {
    // Тип обещал JSON, а тело оборвалось на полуслове.
    throw new ApiError(unreadableProblem(response.status, requestId));
  }
}

/**
 * Один запрос к API. Возвращает разобранное тело или бросает `ApiError`
 * с проблемой целиком — вызывающий код ветвится по `code`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body } = await requestWith<T>(path, options);
  return body;
}

/**
 * Забрать файл, за который сервер отвечает перенаправлением.
 *
 * Нужен потому, что `<a href="…/download" download>` глотает любой отказ:
 * браузер на download-ссылке не показывает ни 503 «Хранилище отчётов не
 * настроено», ни 409 «Отчёт ещё не собран», ни 404 — ни сообщения, ни
 * индикатора, ни записи в консоли. Клиент жмёт и решает, что сломался его
 * браузер. Приёмка нашла ровно это на «Скачать» в центре отчётов.
 *
 * `redirect: 'manual'` — чтобы отличить согласие сервера от отказа, не уводя
 * браузер заранее. При перенаправлении ответ непрозрачный (`opaqueredirect`,
 * статус 0, `Location` не читается), и это значит «сервер согласился» — дальше
 * файл забирает сам браузер обычным переходом. Всё остальное разбирается как
 * обычная проблема RFC 9457 и показывается человеку словами.
 */
export async function downloadFile(path: string): Promise<void> {
  return followServerRedirect(path);
}

/**
 * Спросить сервер и уйти по его перенаправлению — но только если он согласился.
 *
 * Общая механика для двух мест, где ответ принадлежит не нам: выгрузка файла и
 * старт входа через провайдера. Полная навигация браузера в обоих случаях
 * означает, что ЛЮБОЙ отказ показывается человеку как есть: голым problem+json
 * в окне браузера (Яндекс ID) или вовсе никак (download-ссылка).
 */
export async function followServerRedirect(path: string): Promise<void> {
  const url = buildUrl(path, undefined);
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual',
      headers: { Accept: 'application/json' },
    });
  } catch {
    throw new ApiError(offlineProblem());
  }
  if (response.type === 'opaqueredirect' || response.ok) {
    window.location.assign(url);
    return;
  }
  throw new ApiError(await readProblem(response));
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/** Ключ идемпотентности. uuid4 — контракт принимает ULID или uuid4. */
export function idempotencyKey(): string {
  return crypto.randomUUID();
}
